import { memo, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, withRouter } from 'react-router-dom';

import PropTypes from 'prop-types';

import { Box, Grid, Link, Tab, Tabs, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { isAdmin } from '../../utils';

const StyledTab = styled(Tab)(() => ({
  padding: 0,
  margin: 0,
  minWidth: 0,
  maxWidth: 'unset',
  marginRight: '0.8rem',
  borderRadius: '1rem 1rem 0 0',
  textTransform: 'none',
}));

const StyledNav = styled('nav')(({ theme }) => ({
  margin: '2rem 0',
  [theme.breakpoints.down('sm')]: {
    margin: '1rem 0',
  },
}));

const styledProps = ['isActive', 'isDisabled', 'isForAdmin'];

const StyledDefaultTab = styled('li', {
  shouldForwardProp: (prop) => !styledProps.includes(prop),
})(({ theme, isActive, isDisabled, isForAdmin }) => ({
  height: '5rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '1rem 1rem 0 0',
  backgroundColor: theme.palette.custom.tab,
  ...(isActive && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  }),
  ...(isDisabled && {
    backgroundColor: theme.palette.grey[100],
    cursor: 'default',
  }),
  ...(isForAdmin && !isActive && { color: theme.palette.custom.adminDark }),
  ...(isForAdmin &&
    isActive && { backgroundColor: theme.palette.custom.adminDark }),
}));

const StyledLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'isDisabled',
})(({ theme, isDisabled }) => ({
  padding: '1rem 2rem 0.6rem 2rem',
  whiteSpace: 'nowrap',
  '&:hover': {
    textDecoration: 'none',
  },
  ...(isDisabled && {
    backgroundColor: theme.palette.grey[100],
    cursor: 'default',
  }),
}));

const getIsActiveTab = (exact, url, currentUrl) => {
  let isActive = false;
  if (exact && url && currentUrl === url) {
    isActive = true;
  }
  if (!exact && url && currentUrl?.includes(url)) {
    isActive = true;
  }
  return isActive;
};

const TabLabel = memo(
  ({ pathname, isForAdmin, isDisabled, url, externalUrl, label, isActive }) => (
    <StyledDefaultTab
      isActive={isActive}
      isDisabled={isDisabled}
      isForAdmin={isForAdmin}
      key={pathname + url}
    >
      <StyledLink
        onClick={(event) => {
          if (isDisabled || externalUrl || url === pathname) {
            event.preventDefault();
          }
          externalUrl && window.open(externalUrl);
        }}
        component={RouterLink}
        to={url}
        color="inherit"
        isDisabled={isDisabled}
      >
        <Grid
          container
          wrap="nowrap"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body1" color="inherit" fontSize="1.8rem">
            {label}
          </Typography>
        </Grid>
      </StyledLink>
    </StyledDefaultTab>
  ),
);

const TabsComponentWithLinks = memo((props) => {
  const { tabData = [], currentUrl } = props;
  const { pathname } = useLocation();

  const [tabValue, setTabValue] = useState(0);

  const tabs = tabData?.filter(({ isHidden, isForAdmin }) => {
    const isNotVisible = isHidden || (isForAdmin && !isAdmin());
    return !isNotVisible;
  });

  useEffect(() => {
    const tabsState = tabs.map(({ exact, url }) =>
      getIsActiveTab(exact, url, currentUrl),
    );
    const activeTabIndex = tabsState.indexOf(true);
    if (tabValue !== activeTabIndex) {
      const index = activeTabIndex >= 0 ? activeTabIndex : 0;
      setTabValue(index);
    }
  }, [currentUrl, tabs.length]);

  if (!tabData?.length) return null;

  return (
    <StyledNav>
      <Tabs
        sx={{ height: '5rem' }}
        TabScrollButtonProps={{
          sx: {
            '&.Mui-disabled': {
              display: 'none',
            },
          },
        }}
        value={tabValue}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        aria-label="tabs"
      >
        {tabs.map((tab, index) => {
          const isActive = getIsActiveTab(tab?.exact, tab?.url, currentUrl);
          const label = (
            <TabLabel {...tab} pathname={pathname} isActive={isActive} />
          );
          return (
            <StyledTab label={label} value={index} key={tab?.url ?? index} />
          );
        })}
      </Tabs>
      <Box
        sx={(theme) => ({
          borderBottom: `0.4rem solid ${theme.palette.primary.main}`,
        })}
      />
    </StyledNav>
  );
});

TabsComponentWithLinks.defaultProps = {
  currentUrl: null,
};

TabsComponentWithLinks.propTypes = {
  tabData: PropTypes.instanceOf(Array).isRequired,
  currentUrl: PropTypes.string,
};

TabLabel.propTypes = {
  pathname: PropTypes.string,
  isForAdmin: PropTypes.bool,
  isDisabled: PropTypes.bool,
  url: PropTypes.string,
  externalUrl: PropTypes.string,
  label: PropTypes.string,
  isActive: PropTypes.bool,
};

export default withRouter(TabsComponentWithLinks);
